
import { Attachment } from '../context/AttachmentContext/types';
import { simplifyFileType } from '../utils/fileTypeUtils';

// Gmail API service for fetching attachments
export const api = {
  // Auth related endpoints
  auth: {
    // Get the current user session
    getSession: async () => {
      const user = localStorage.getItem('gmail-attachment-user');
      return user ? JSON.parse(user) : null;
    },
    
    // Exchange OAuth code for tokens (for reference only - handled in AuthContext)
    getToken: async (code: string) => {
      // For production, this should be called from a backend service
      // For now, we're handling this in AuthContext directly
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      const clientSecret = "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"; // Re-added for OAuth to work
      const redirectUri = import.meta.env.VITE_REDIRECT_URI;
      
      const params = new URLSearchParams();
      params.append('code', code);
      params.append('client_id', clientId);
      params.append('client_secret', clientSecret);
      params.append('redirect_uri', redirectUri);
      params.append('grant_type', 'authorization_code');
      
      // This is a simplified approach and would be better implemented
      // using a secure backend or serverless function
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params
      });
      
      if (!response.ok) {
        throw new Error('Failed to exchange code for token');
      }
      
      return response.json();
    },
    
    // Refresh token when access token expires
    // Note: For a production app, token refresh should be handled server-side
    refreshToken: async (refreshToken: string) => {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      const clientSecret = "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"; // Re-added for OAuth to work
      
      // In a production environment, this should be handled by a backend service
      // to keep your client secret secure
      const params = new URLSearchParams();
      params.append('refresh_token', refreshToken);
      params.append('client_id', clientId);
      params.append('client_secret', clientSecret);
      params.append('grant_type', 'refresh_token');
      
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }
      
      return response.json();
    },
    
    // Logout by clearing the session
    logout: async () => {
      localStorage.removeItem('gmail-attachment-user');
      return { success: true };
    }
  },
  
  // Attachments related endpoints
  attachments: {
    // Get all attachments from Gmail
    getAll: async (accessToken: string) => {
      // 1. First, get messages with real attachments, excluding promotional emails likely to have footer images
      // Build query to include messages from up to 2 years ago
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      const dateString = twoYearsAgo.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      const query = `has:attachment -category:promotions -category:social after:${dateString}`;
      console.log('Fetching messages with query:', query);
      
      const messagesResponse = await fetch(
        `https://www.googleapis.com/gmail/v1/users/me/messages?q=${encodeURIComponent(query)}&maxResults=100`, 
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      if (!messagesResponse.ok) {
        throw new Error('Failed to fetch messages with attachments');
      }
      
      const messagesData = await messagesResponse.json();
      
      if (!messagesData.messages || messagesData.messages.length === 0) {
        return { data: [] };
      }
      
      // 2. Get details for each message with attachments (increased limit from 30 to 100)
      const attachmentsPromises = messagesData.messages.slice(0, 100).map(async (message: { id: string }) => {
        const messageDetailResponse = await fetch(
          `https://www.googleapis.com/gmail/v1/users/me/messages/${message.id}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        
        if (!messageDetailResponse.ok) {
          console.error(`Failed to fetch details for message ${message.id}`);
          return [];
        }
        
        const messageDetail = await messageDetailResponse.json();
        const subject = messageDetail.payload.headers.find((h: { name: string }) => h.name === 'Subject')?.value || 'No Subject';
        const from = messageDetail.payload.headers.find((h: { name: string }) => h.name === 'From')?.value || 'Unknown';
        const date = messageDetail.payload.headers.find((h: { name: string }) => h.name === 'Date')?.value || 'Unknown Date';
        
        // Extract attachments from the message
        const attachments: Attachment[] = [];
        
        // Function to recursively find attachments in parts while filtering out likely footer images
        const findAttachments = (parts: any[]) => {
          if (!parts) return;
          
          for (const part of parts) {
            if (part.filename && part.filename.trim() !== '') {
              // Skip likely footer images, signatures, and small icons
              const lowerFileName = part.filename.toLowerCase();
              // Expanded list of keywords that suggest non-attachment images
              const isLikelyFooterImage = 
                lowerFileName.includes('logo') || 
                lowerFileName.includes('footer') || 
                lowerFileName.includes('signature') || 
                lowerFileName.includes('banner') ||
                lowerFileName.includes('social') ||
                lowerFileName.includes('icon') ||
                lowerFileName.includes('badge') ||
                lowerFileName.includes('avatar') ||
                lowerFileName.includes('brand') ||
                lowerFileName.includes('card') ||
                // Increase size threshold for images - signatures and business cards are often under 30KB
                (part.mimeType.startsWith('image/') && parseInt(part.body.size) < 30000)
              
              // Additional heuristic checks for detecting signatures
              let shouldSkip = isLikelyFooterImage;
              
              // Check if this is part of an HTML email with inline images (likely signatures)
              if (part.mimeType.startsWith('image/') && part.headers) {
                // Check for content disposition or content ID headers that suggest inline images
                const contentDisposition = part.headers.find((h: { name: string }) => 
                  h.name.toLowerCase() === 'content-disposition'
                )?.value || '';
                
                const contentId = part.headers.find((h: { name: string }) => 
                  h.name.toLowerCase() === 'content-id'
                )?.value || '';
                
                // If it's marked as inline or has a content-id, it's likely a signature or embedded image
                if (contentDisposition.includes('inline') || contentId) {
                  shouldSkip = true;
                }
              }
              
              // Only add real attachments that aren't signatures or footer images
              if (part.body.attachmentId && !shouldSkip) {
                attachments.push({
                  id: part.body.attachmentId,
                  emailId: message.id,
                  fileName: part.filename,
                  fileSize: parseInt(part.body.size) || 0,
                  fileType: simplifyFileType(part.mimeType, part.filename),
                  emailSubject: subject,
                  emailDate: new Date(date).toISOString(),
                  emailSender: from,
                  emailSenderName: from.split('<')[0].trim(),
                  emailRecipients: [],
                  downloadUrl: `https://www.googleapis.com/gmail/v1/users/me/messages/${message.id}/attachments/${part.body.attachmentId}`
                });
              }
            }
            
            if (part.parts) {
              findAttachments(part.parts);
            }
          }
        };
        
        if (messageDetail.payload.parts) {
          findAttachments(messageDetail.payload.parts);
        }
        
        return attachments;
      });
      
      const attachmentsArrays = await Promise.all(attachmentsPromises);
      const allAttachments = attachmentsArrays.flat();
      
      return { data: allAttachments };
    },
    
    // Download a single attachment
    download: async (id: string, emailId: string, accessToken: string) => {
      const response = await fetch(
        `https://www.googleapis.com/gmail/v1/users/me/messages/${emailId}/attachments/${id}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to download attachment ${id}`);
      }
      
      const data = await response.json();
      
      // Gmail API returns base64 encoded data
      const attachmentData = data.data.replace(/-/g, '+').replace(/_/g, '/');
      const decodedData = atob(attachmentData);
      
      return { 
        success: true, 
        data: decodedData,
        message: `Downloaded attachment ${id}` 
      };
    },
    
    // Download multiple attachments (would require server-side implementation for ZIP creation)
    downloadBulk: async (attachments: { id: string, emailId: string, fileName: string }[], accessToken: string) => {
      // In a real implementation, you would have a server endpoint that
      // fetches multiple attachments and returns them as a ZIP file
      // For client-side, we'll download them one by one
      
      const downloads = await Promise.all(
        attachments.map(att => api.attachments.download(att.id, att.emailId, accessToken))
      );
      
      return { 
        success: true, 
        data: downloads.map(d => d.data),
        message: `Downloaded ${attachments.length} attachments` 
      };
    }
  }
};
